import { apiGet, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseDividendPaymentTimeline {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private currentInterval: string = '1w';

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        const symbols = this.fetchSymbols();
        return {
      headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        await this.attachStyles();
        this.currentData = null;
        this.updateDisplay();
        this.emit('ready', {});
        this.config.onReady?.();
        this.setupChart();
        cancelSelectEvent(this.shadowRoot);
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const widgetHtml = `
            <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}" style="width:${this.config.width};height:${this.config.height};">
                <div class="headTop">
                    <h4 class="title market">${this.getTitleIconSvg()}
                        Dividend Payment Timeline
                    </h4>
                </div>
                <div class="inBody">
                    <div class="optionsRow" data-testing-id="options-row">
                        <div class="item" data-testing-id="frequency-selector">
                            <select id="p_frequency" name="p_frequency" data-testing-id="frequency-select">
                                <option value="Daily">Daily</option>
                                <option value="Weekly">Weekly</option>
                                <option value="Monthly">Monthly</option>
                                <option value="Quarterly">Quarterly</option>
                                <option value="Annual">Annual</option>
                            </select>
                        </div>
                        <span ></span>
                        <div class="item" data-testing-id="interval-selector">
                            <select id="p_interval" name="p_interval" data-testing-id="interval-select">
                                <option value="day">Day</option>
                                <option value="week">Week</option>
                                <option value="month">Month</option>
                                <option value="quarter">Quarter</option>
                                <option value="year">Year</option>
                            </select>
                        </div>
                    </div>
                    <div class="chartDv" id="dividendPaymentContainer" data-testing-id="dividend-container"></div>
                </div>
            </div>
        `;
        this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

        const selInterval = this.shadowRoot.querySelector('#p_interval') as HTMLSelectElement | null;
        const selFrequency = this.shadowRoot.querySelector('#p_frequency') as HTMLSelectElement | null;
        if (selInterval) {
            selInterval.value = 'week';
            selInterval.addEventListener('change', () => this.setupChart());
        }
        if (selFrequency) {
            selFrequency.value = 'Weekly';
            selFrequency.addEventListener('change', () => this.setupChart());
        }
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async setupChart(): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        try {
            const interval = (this.shadowRoot.querySelector('#p_interval') as HTMLSelectElement | null)?.value || 'week';
            const frequency = (this.shadowRoot.querySelector('#p_frequency') as HTMLSelectElement | null)?.value || 'Weekly';
            const url = `${APIURL}/v1/discover/dividend-payment-timeline?p_interval=${encodeURIComponent(interval)}&p_frequency=${encodeURIComponent(frequency)}`;

            const response = await apiGet({
                method: 'GET',
                url,
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) throw new Error("Failed to fetch data");
            const data = await response.json();
            const items = Array.isArray(data) ? data : (data && (data.data || data.items || data.results)) || [];
            this.currentData = items;
            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error("Error fetching chart data:", err);
            this.currentData = [];
            this.updateDisplay();
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!this.currentData) {
            return;
        }
        this.renderAnalystRating();
    }

    private renderAnalystRating(): void {
        const container = this.shadowRoot.querySelector('#dividendPaymentContainer');
        if (!container) return;
        const items = this.currentData?.timeline || [];
        const hasData = Array.isArray(items) && items.length > 0;

    // ❌ No data case
    if (!hasData) {
        container.removeAttribute('data-testing-id');
        container.innerHTML = getNoDataHTML();
        return;
    }

    // ✅ Data exists → attribute show only here
    container.setAttribute('data-testing-id', 'dividend-payment-timeline-chartdv');

        if (!Array.isArray(items) || items.length === 0) {
            container.innerHTML = getNoDataHTML();
            return;
        }
        
        const paymentsList = items.flatMap((it: any) => {
            const payments = Array.isArray(it.payments) ? it.payments : [];
            return payments.map((p: any) => ({
                ...p,
                payment_count: it.payment_count,
                avg_yield: it.avg_yield,
                total_amount: it.total_amount,
                period: it.period,
            }));
        });

        const groups: Record<string, any[]> = {};
        paymentsList.forEach((p: any) => {
            const key = p.payment_date || p.period || 'Unknown';
            if (!groups[key]) groups[key] = [];
            groups[key].push(p);
        });

        const sortedDates = Object.keys(groups).sort();
        const colors = ['#FF1111', '#4702DF', '#00B12C', '#00B597', '#6000b1', '#b1003f', '#b16800', '#004cb1'];
        const colorFor = (s: string) => {
            let h = 0; for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i);
            return colors[Math.abs(h) % colors.length];
        };
        const fmt = (v: any) => Number(v).toFixed(2);

        let html = `
            <div class="dividendPaymentMain" data-testing-id="dividend-payment-main">
                <div class="inner" data-testing-id="dividend-payment-inner">
        `;
        
        for (let idx = 0; idx < sortedDates.length; idx++) {
            const dateKey = sortedDates[idx];
            const group = groups[dateKey];
            const total = group.reduce((acc, g) => acc + (parseFloat(g.amount) || 0), 0);
            const paymentCount = group.length;
            const avgYield = paymentCount ? (group.reduce((acc, g) => acc + (parseFloat(g.yield) || parseFloat(g.avg_yield) || 0), 0) / paymentCount) : 0;
            
            let pillsHtml = '';
            for (let pIdx = 0; pIdx < group.length; pIdx++) {
                const p = group[pIdx];
                const bg = colorFor(p.symbol || '');
                pillsHtml += `
                    <div class="pillBox" style="background:${bg}" data-testing-id="pill-${idx}-${pIdx}" data-symbol="${p.symbol}" data-amount="${p.amount}">
                        <h4 data-testing-id="pill-symbol">${p.symbol} <span data-testing-id="pill-amount">$${fmt(p.amount)}</span></h4>
                    </div>
                `;
            }

            html += `
                <div class="item" 
                     data-testing-id="dividend-item-${idx}" 
                     data-index="${idx}"
                     data-date="${dateKey}"
                     data-payment-count="${paymentCount}"
                     data-avg-yield="${avgYield}"
                     data-total-amount="${total}">
                    <div class="mainRow rowitm">
                        <div class="colItm">
                            <p data-testing-id="item-date-info"><strong data-testing-id="item-date">${dateKey}</strong> <span data-testing-id="item-payment-count">${paymentCount} Payments</span> - Avg Yield <span data-testing-id="item-avg-yield">${fmt(avgYield)}%</span></p>
                        </div>
                        <div class="colItm text-right">
                            <p data-testing-id="item-total-label"><strong class="text-green" data-testing-id="item-total-amount">$${fmt(total)}</strong> Total</p>
                        </div>
                    </div>
                    <div class="boxRow rowitm" data-testing-id="item-pills-container">${pillsHtml}</div>
                </div>
            `;
        }
        
        html += `
                </div>
            </div>
        `;

        container.innerHTML = html;
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseDividendPaymentTimeline: typeof TraderverseDividendPaymentTimeline; } }
if (typeof window !== 'undefined') { window.TraderverseDividendPaymentTimeline = TraderverseDividendPaymentTimeline; }